// import { API_GetUserResponse } from "@/interfaces/api/user/api_user_login_types.types"
import { API_Error, API_Response, API_Success_Response } from "@/interfaces/api/response/api_response_types"
import { fetcher, toBase64, toBase64URI } from "@/utils/helpers"
import { ResetPasswordData, validateCodeData } from "@/interfaces/api/login/forgotPassword/api_request_types"
import { API_Request_Code_Response, API_Reset_Password_Response, API_Validate_Code_Response } from "@/interfaces/api/login/forgotPassword/api_response_types"
import { API_Data_DynamicUnsubscribeURL_Response, API_Data_Edit_Self_Response, API_Data_Self_Response } from "@/interfaces/api/self/api_response_types"
import { EditSelfData } from "@/interfaces/api/self/api_request_types"
import { API_Data_DynamicChangePasswordURL_Response, ChangePasswordData } from "@/interfaces/api/change-password/api_request_types"
import { SignUpData } from "@/interfaces/api/signup/api_request_types"
import { API_Data_Reseller_Autologin_Response } from "@/interfaces/api/resellerAutologin/api_response_types"
import { ResellerAutologinData, ResellerAutologinPayload } from "@/interfaces/api/resellerAutologin/api_request_types"
import { API_Data_DynamicSignUpURL_Response } from "@/interfaces/api/signup/api_response_types"
import { API_Data_DynamicRecoverPasswordURL_Response } from "@/interfaces/api/recover-password/api_request_types"

/*
==================
      USER
==================
*/

/**
 * Performs a sign-up request against the server using HTTP Basic Authentication.
 * 
 * @param param0 - An object containing the Basic Auth credentials and the sign-up data payload 
 * @returns The API response returned by the server
 * @throws {API_Error} If the request fails or the API returns an error.
 */
export async function signUp ({ basicAuth, payload }:{ basicAuth: string, payload: SignUpData }): Promise<API_Success_Response<undefined>> {
  const res = await fetcher("/api/signup", { 
    method: 'POST',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + basicAuth
    },
    body: JSON.stringify(payload),
    credentials: 'include'
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknonw signup error' } as API_Error
  return response
}

/**
 * Gets the dynamic sign up URL from the server
 * 
 * @returns The API response returned by the server
 * @throws {API_Error} If the request fails or the response is invalid.
 */
export async function getDynamicSignUpURL(): Promise<API_Data_DynamicSignUpURL_Response> {
  // return { url: 'https://ludiumlab.com' } // TODO: Missing in development backend, return a default value
  
  const res = await fetcher(`/api/subscribe`, {
    method: 'GET'
  })
  const response = await res.json() as API_Response<API_Data_DynamicSignUpURL_Response>
    
  if (!res.ok) throw { code: res.status, description: 'Unknow dynamic sign-up url error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Dynamic sign-up URL data missing in response' } as API_Error

  return response.data
  
}

/**
 * Performs a login request against the server using HTTP Basic Authentication.
 *
 * Sends a POST request to `/api/login` with the provided Basic Auth credentials.
 * The request includes cookies (`credentials: 'include'`) so the server can
 * set or read session cookies.
 * 
 * @param basicAuth - Base64-encoded credentials in the form "username:password" (without the "Basic " prefix)
 * @returns The API response returned by the server
 */
export async function login (basicAuth: string): Promise<API_Success_Response<undefined>> {
  const res = await fetcher("/api/login", { 
    method: 'POST',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + basicAuth
    },
    credentials: 'include'
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknow login error' } as API_Error
  return response
}

/**
 * Logs out the current user by calling the logout API route.
 * The API endpoint is responsible for clearing the authentication cookie
 * (usually an HttpOnly cookie) on the server.
 * 
 * This request must NOT be cached, as it performs a side effect.
 * 
 * @returns The API response returned by the server
 */
export async function logout(): Promise<API_Success_Response<undefined>> {
  const res = await fetcher("/api/logout", { 
    method: 'POST',
    cache: "no-store",
    credentials: 'include'
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknow logout error' } as API_Error
  return response
}

/**
 * Change the user’s password on the server
 *  
 * @param param0 - An object containing the new password and the basicAuth
 * @returns 
 */
export async function changePassword ({ basicAuth, payload}: { basicAuth: string, payload: ChangePasswordData }): Promise<API_Success_Response<undefined>> {
  const res = await fetcher("/api/change-password", { 
    method: 'POST',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + basicAuth,
    },
    body: JSON.stringify(payload),
    credentials: 'include'
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknow login error' } as API_Error
  return response
}

/**
 * Gets the dynamic change password URL from the server
 * 
 * @returns The API response returned by the server
 * @throws {API_Error} If the request fails or the response is invalid.
 */
export async function getDynamicChangePasswordURL (userId: string): Promise<API_Data_DynamicChangePasswordURL_Response> {
  // return { url: 'https://ludiumlab.com' } // TODO: Missing in development backend, return a default value
  
  const res = await fetcher(`/api/users/${userId}/change-password`, {
    method: 'POST',
    credentials: 'include'
  })
  const response = await res.json() as API_Response<API_Data_DynamicChangePasswordURL_Response>
    
  if (!res.ok) throw { code: res.status, description: 'Unknow dynamic change password url error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Dynamic change password URL data missing in response' } as API_Error

  return response.data
}

/**
 * Gets the dynamic change password URL from the server
 * 
 * @returns The API response returned by the server
 * @throws {API_Error} If the request fails or the response is invalid.
 */
export async function getDynamicRecoverPasswordURL(): Promise<API_Data_DynamicRecoverPasswordURL_Response> {
  // return { url: 'https://ludiumlab.com' } // TODO: Missing in development backend, return a default value
  
  const res = await fetcher(`/api/forget-password`, {
    method: 'GET'
  })
  const response = await res.json() as API_Response<API_Data_DynamicRecoverPasswordURL_Response>
    
  if (!res.ok) throw { code: res.status, description: 'Unknow dynamic recover password url error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Dynamic recover password URL data missing in response' } as API_Error

  return response.data
}

/**
 * Requests a verify email code for the given username by calling the corresponding API route
 * 
 * @param userName - The username for which to request the verify email code
 * @returns The API response returned by the server
 */
export async function requestVerifyEmailCode ({ userID, basicAuth }: { userID: string, basicAuth: string }): Promise<API_Success_Response<undefined>> {
  const res = await fetcher(`/api/users/${userID}/send-email-verification`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + basicAuth
    }
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when trying to send the code' } as API_Error
  return response
}

/**
 * Requests a verify email code for the given username by calling the corresponding API route
 * 
 * @param userName - The username for which to request the verify email code
 * @returns The API response returned by the server
 */
export async function completeAccountRequestVerifyEmailCode (): Promise<API_Success_Response<undefined>> {
  const res = await fetcher(`/api/self/login-methods/email/send-verification-code`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: "include"
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when trying to send the code' } as API_Error
  return response
}

/**
 * Requests a verify email code for the given username by calling the corresponding API route
 * 
 * @param userName - The username for which to request the verify email code
 * @returns The API response returned by the server
 */
export async function verifyEmailCode ({ userID, basicAuth, token }: { userID: string, basicAuth: string, token: string }): Promise<API_Success_Response<undefined>> {
  const res = await fetcher(`/api/users/${userID}/veri?token=${token}`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + basicAuth
    }
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when verifying email' } as API_Error
  return response
}

/**
 * Requests a verify email code for the given username by calling the corresponding API route
 * 
 * @param userName - The username for which to request the verify email code
 * @returns The API response returned by the server
 */
export async function completeAccountVerifyEmailCode ({ token }: { token: string }): Promise<API_Success_Response<undefined>> {
  const res = await fetcher(`/api/self/login-methods/email/verify-code?token=${token}`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: "include"
  })

  const response = await res.json() as API_Response<undefined>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when verifying email' } as API_Error
  return response
}


/**
 * Requests a password reset code for the given username by calling the corresponding API route
 * 
 * @param userName - The username for which to request the password reset code
 * @returns The API response returned by the server
 */
export async function requestPasswordCode (userName: string): Promise<API_Success_Response<API_Request_Code_Response>> {
  const res = await fetcher(`/api/passes/${userName}`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    }
  })

  const response = await res.json() as API_Response<API_Request_Code_Response>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when trying to send the code' } as API_Error
  return response
}

/**
 * Validates a password reset code for a given username
 * 
 * @param param0 - An object containing the username and the code to validate
 * @returns The API response returned by the server
 */
export async function validatePasswordCode ({ userName, code }: validateCodeData): Promise<API_Success_Response<API_Validate_Code_Response>> {
  const basicAuth = toBase64(`${userName}:${code}`)

  const res = await fetcher(`/api/passes/${toBase64URI(userName)}/validate`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + basicAuth
    }
  })

  const response = await res.json() as API_Response<API_Validate_Code_Response>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when trying to validate the password reset code' } as API_Error
  return response
}

/**
 * Resets the password for a given username using a valid reset code
 * @param param0 - An object containing the username, code and new password
 * @returns The API response returned by the server
 */
export async function resetPassword ({ userName, code, body }: ResetPasswordData): Promise<API_Success_Response<API_Reset_Password_Response>> {
  const basicAuth = toBase64(`${userName}:${code}`)

  const res = await fetcher(`/api/passes/${toBase64URI(userName)}/reset`, { 
    method: 'PUT',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + basicAuth
    },
    body: JSON.stringify(body)
  })

  const response = await res.json() as API_Response<API_Reset_Password_Response>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown error when trying to reset the password' } as API_Error
  return response
}

/**
 * Fetches the authenticated user's data from the backend.
 * 
 * @returns The authenticated user's full profile data.
 * @throws {API_Error} If the request fails or the API returns an error.
 */
export async function getSelf (): Promise<API_Data_Self_Response> {
  const res = await fetcher("/api/self", { cache: "no-store" })
  const response = await res.json() as API_Response<API_Data_Self_Response>

  if (!res.ok) throw { code: res.status, description: 'Unknonw self error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'User data missing in response' } as API_Error

  return response.data
}

/**
 * Edits a self's data on the server
 * 
 * @param param0 - An object that contains the profileId and a payload with the data to be sent to the server
 * @returns - Returns the data of the edited profile
 */
export async function editSelf (payload: EditSelfData): Promise<API_Data_Edit_Self_Response> {
  const res = await fetcher(`/api/self`, { 
    method: 'PUT',
    cache: "no-store",
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  })
  const response = await res.json() as API_Response<API_Data_Edit_Self_Response>
  if (!res.ok) throw response.error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Add profile data missing in response' } as API_Error

  const finalData = response.data
  if (response.error) {
    finalData.error = response.error
  }
  return finalData
}

/**
 * Delete user account on the server
 *  
 * @param param0 - An object containing the basicAuth
 * @returns 
 */
export async function deleteUser ({ basicAuth }: { basicAuth: string }): Promise<API_Success_Response<undefined>> {
  const res = await fetcher("/api/self/delete", { 
    method: 'POST',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + basicAuth,
    }
  })

  const response = await res.json() as API_Response<undefined>

  if (!res.ok) throw { code: res.status, description: 'Unknow delete user account error' } as API_Error
  if (response.status === 'error') throw response.error
  
  return response
}

/**
 * Gets the dynamic unsubscribe URL from the server
 * 
 * @returns The API response returned by the server
 * @throws {API_Error} If the request fails or the response is invalid.
 */
export async function getDynamicUnsubscribeURL(userId: string): Promise<API_Data_DynamicUnsubscribeURL_Response> {
  // return { url: 'https://ludiumlab.com' } // TODO: Missing in development backend, return a default value
  
  const res = await fetcher(`/api/users/${userId}/unsubscribe`, {
    method: 'POST',
    credentials: 'include'
  })
  const response = await res.json() as API_Response<API_Data_DynamicUnsubscribeURL_Response>
    
  if (!res.ok) throw { code: res.status, description: 'Unknow dynamic unsubscribe url error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Dynamic unsubscribe URL data missing in response' } as API_Error

  return response.data
}

export async function resellerAutologin ({ reseller, token }: ResellerAutologinPayload): Promise<API_Success_Response<API_Data_Reseller_Autologin_Response>> {

  const res = await fetcher(`/api/reseller-login/${reseller}?token=${token}`, { 
    method: 'POST',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: "include"
  })

  const response = await res.json() as API_Response<API_Data_Reseller_Autologin_Response>

  if (response.status === 'error') throw response.error
  if (!res.ok) throw { code: res.status, description: 'Unknown reseller autologin error' } as API_Error
  return response
}